import { useState, useEffect, useMemo } from "react";
import { getCurrentFonts } from "./getCurrentFonts";
import rootElement from "../../../../rootElement";

const FONTFACE_PROPERTIES = [
  'family',
  'weight',
  'style',
] as const;

export type FontFaceDescriptor = Record<(typeof FONTFACE_PROPERTIES)[number], string>;

/*
 * A hook that handles the complexities of getting the font faces the browser is aware of from both
 * our app and the URL the user enters in the returned component.
 *
 * @return An object with
 *   - `fontFaces` a list of objects describing the font faces
 *   - `EmbedURL` a component with the UI where the user can enter an URL we will fetch and process
 */
export function useFontFaces() {
  const [_fontFaces, setFontFaces] = useState<FontFace[]>(getCurrentFonts());

  // Deduplicate the font faces by the properties we care about (FONTFACE_PROPERTIES), because there
  // are duplicates since each font face has a Unicode range
  const fontFaces = useMemo(
    () => [...new Map(
      (_fontFaces.filter(Boolean) as FontFace[])
        .map((ff) => {
          return [
            JSON.stringify(Object.fromEntries(FONTFACE_PROPERTIES.map((prop) => [prop, ff[prop]]))),
            ff,
          ];
        }),
    ).values()],
    [_fontFaces],
  );

  useEffect(() => {
    const load = () => {
      setFontFaces(getCurrentFonts());
    };

    document.fonts.addEventListener('loadingdone', load);
    return () => {
      document.fonts.removeEventListener('loadingdone', load);
    };
  }, []);

  const simplifiedFontFaces = useMemo(
    () => fontFaces.map((ff) => Object.fromEntries(FONTFACE_PROPERTIES.map((prop) => {
      if (prop === 'family') {
        return [prop, String(ff[prop]).replace(/^ *\\?['"]?(.*?)\\?['"]? *$/, '$1')]; // Remove surrounding space and quotes
      } else {
        return [prop, ff[prop]];
      }
    }))),
    [fontFaces],
  ) as FontFaceDescriptor[];

  const selectedFontFace = useMemo(
    () => {
      const wrapper = rootElement.querySelector('#wrapper');
      if (!wrapper) return;

      const computedStyle = getComputedStyle(wrapper);

      // Extract the properties we care from the `computedStyle` so it's in the same format as `simplifiedFontFaces`
      const computedFontFace =  Object.fromEntries(
        FONTFACE_PROPERTIES.map((prop) => {
          if (prop === 'family') {
            return [
              prop,
              computedStyle.fontFamily
                .split(',')[0]                                 // Get the first font-family declared
                .replace(/^ *\\?['"]?(.*?)\\?['"]? *$/, '$1'), // Remove surrounding space and quotes
            ];
          } else {
            // @ts-ignore
            const val: string = computedStyle['font-' + prop];
            return [prop, val];
          }
        }),
      ) as FontFaceDescriptor;

      // Find the element of `simplifiedFontFaces` that most closely matches the `computedFontFace`.
      // This is needed because the computed value does not necessarily match the declared one. In
      // particular, the `weight` of the registered font may be `normal`, but the computed may be `400`
      const [{ fontFace: selectedFontFace }] = simplifiedFontFaces
        .map((fontFace) => ({
          fontFace,
          similarity: FONTFACE_PROPERTIES
            .map((prop) => fontFace[prop] === computedFontFace[prop]
              ? ({ family: 2, weight: 1, style: 2 }[prop])
              : 0,
            )
            .reduce((acc: number, val) => acc + val, 0),
        })).toSorted((a, b) => b.similarity - a.similarity);

      return selectedFontFace;
    },
    [simplifiedFontFaces],
  ) as FontFaceDescriptor;

  return {
    fontFaces: simplifiedFontFaces,
    selectedFontFace,
  };
}
